import { CalendarCheck, Phone, Truck, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";

const HowItWorks = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [expandedStep, setExpandedStep] = useState<number | null>(null);
  const isMobile = useIsMobile();

  const steps = [
    {
      icon: CalendarCheck,
      number: "01",
      title: "Book",
      description: "Pick a time. Takes 2 minutes.",
      detail: "Choose a slot that works for you. We're open 5 days a week. Just pop in a few details and we'll send you a confirmation email. Heads up, we might ask for your blood pressure, height and weight depending on your pill.",
    },
    {
      icon: Phone,
      number: "02",
      title: "We'll Call You",
      description: "A quick, friendly chat with our pharmacist.",
      detail: "One of our contraception specialist pharmacists will give you a ring and ask a few simple questions to make sure everything's right for you. It's relaxed, private and usually done in under 10 minutes. Supply is subject to eligibility.",
    },
    {
      icon: Truck,
      number: "03",
      title: "Receive",
      description: "Plain packaging, straight to your door.",
      detail: "Your contraception arrives in unmarked packaging so no one knows what's inside. It's dispatched by Pharmacy Services Direct and delivered tracked by Royal Mail. Usually takes 1 to 3 days.",
    },
  ];

  useEffect(() => {
    if (expandedStep !== null) return;
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % steps.length);
    }, 2500);

    return () => clearInterval(interval);
  }, [steps.length, expandedStep]);

  const toggleExpand = (index: number) => {
    setExpandedStep(expandedStep === index ? null : index);
  };

  return (
    <section id="how-it-works" className="py-16 px-6 bg-surface-blush lg:py-24">
      <div className="max-w-sm lg:max-w-6xl xl:max-w-7xl mx-auto">
        <p className="text-tiny uppercase tracking-[0.2em] text-primary mb-3 animate-fade-up font-medium lg:text-center">
          Simple process
        </p>
        <h2 className="text-2xl lg:text-4xl font-bold text-foreground mb-8 lg:mb-12 animate-fade-up leading-tight lg:text-center">
          Three steps. That's it.
        </h2>

        {/* Mobile: stacked cards */}
        <div className="space-y-4 lg:hidden">
          {steps.map((step, index) => {
            const isActive = activeStep === index;
            const isExpanded = expandedStep === index;
            
            return (
              <div 
                key={step.number}
                className={`relative overflow-hidden rounded-2xl transition-all duration-500 animate-fade-up flex flex-col ${
                  isActive || isExpanded
                    ? 'shadow-[0_8px_30px_rgb(0,0,0,0.12)] scale-[1.02]' 
                    : 'shadow-[0_2px_15px_rgb(0,0,0,0.08)]'
                }`}
                style={{ animationDelay: `${0.1 + index * 0.1}s` }}
              >
                <div className="flex">
                  <div className="bg-coral flex items-center justify-center px-4 min-w-[60px]">
                    <span className="text-4xl font-bold text-white">{index + 1}</span>
                  </div>
                  <div className="flex-1 p-4 bg-white">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-8 h-8 rounded-full bg-coral/10 flex items-center justify-center">
                        <step.icon className="w-4 h-4 text-coral" />
                      </div>
                      <h3 className="text-lg font-bold text-navy">{step.title}</h3>
                      <button
                        onClick={() => toggleExpand(index)}
                        className="ml-auto w-7 h-7 rounded-full bg-coral/10 hover:bg-coral/20 flex items-center justify-center transition-colors"
                        aria-label={isExpanded ? "Collapse" : "Expand"}
                      >
                        <ChevronDown className={`w-4 h-4 text-coral transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
                      </button>
                    </div>
                    <p className="text-sm text-muted-foreground">{step.description}</p>
                  </div>
                </div>
                <div className={`bg-white px-5 overflow-hidden transition-all duration-400 ease-out ${isExpanded ? 'max-h-52 py-3 border-t border-coral/10' : 'max-h-0 py-0'}`}>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-3">{step.detail}</p>
                  <a href="#is-this-for-me">
                    <button className="text-xs font-semibold text-primary-foreground bg-primary rounded-full px-4 py-1.5 hover:opacity-90 transition-opacity">
                      Book now →
                    </button>
                  </a>
                </div>
              </div>
            );
          })}
        </div>

        {/* Desktop: expanded tall cards with detail always visible */}
        <div className="hidden lg:grid lg:grid-cols-3 lg:gap-8">
          {steps.map((step, index) => {
            const isActive = activeStep === index;
            
            return (
              <div 
                key={step.number}
                className={`relative overflow-hidden rounded-3xl transition-all duration-500 animate-fade-up bg-white ${
                  isActive
                    ? 'shadow-[0_12px_40px_rgb(0,0,0,0.12)] scale-[1.02]' 
                    : 'shadow-[0_4px_20px_rgb(0,0,0,0.06)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.1)] hover:scale-[1.01]'
                }`}
                style={{ animationDelay: `${0.1 + index * 0.1}s` }}
              >
                {/* Large coral header band */}
                <div className="bg-coral px-8 py-6 flex items-center gap-4">
                  <span className="text-5xl font-bold text-white/90">{index + 1}</span>
                  <div>
                    <h3 className="text-xl font-bold text-white">{step.title}</h3>
                    <p className="text-sm text-white/80 mt-0.5">{step.description}</p>
                  </div>
                </div>
                
                {/* Icon + detail */}
                <div className="px-8 py-6">
                  <div className="w-12 h-12 rounded-xl bg-coral/10 flex items-center justify-center mb-4">
                    <step.icon className="w-6 h-6 text-coral" />
                  </div>
                  <p className="text-base text-muted-foreground leading-relaxed">
                    {step.detail}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Link to full page */}
        <div className="mt-6 text-center lg:mt-10">
          <Link
            to="/how-it-works/"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-primary/80 transition-colors underline underline-offset-2"
          >
            Learn more about how it works
          </Link>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
